import type { ApolloClient } from "@apollo/client";

import { type UserResponseType } from "@api/session";

import { isCustomerAdmin, userFeatureEnabled } from "@/abilities/helpers";
import client from "@/apollo";
import { removeAuthorizationToken, setAuthorizationCookie, setAuthorizationToken } from "@/authorization_token";
import { setCustomer } from "@/components/app/Switcher/switcherReducer";
import { setAllCustomers, setMobileView } from "@/components/roster/rosterReducer";
import { checkForUnconfirmedWorkTimes } from "@/components/staff/Worktimes/worktimeActions";
import { resetCapacity } from "@/components/stats/statsReducer";
import { LoginDocument, RefreshDocument, RefreshUserProtocolBeaconDocument, SessionDataDocument } from "@/gql/graphql";
import { notBlank } from "@/new_utils";
import { type AppThunk } from "@/store";
import * as reducers from "@/store/sessionReducer";

import { getAcdToken, getEndpointToken, resetAcdToken } from "./acdActions";

const UNAUTHORIZED_PATHS = ["/", "/confirm-event", "/confirm-todo", "/login"];

export const getCustomerIdentifier = () =>
  UNAUTHORIZED_PATHS.includes(document.location.pathname)
    ? undefined
    : document.location.pathname.split("/").filter(notBlank)[0];

export const setCurrentUser = (user: Nullable<SessionUser>): AppThunk => {
  return (dispatch) => {
    if (user) {
      dispatch(getEndpointToken());
    }

    dispatch(reducers.setCurrentUser(user));
  };
};

const withTypedUserAttrs = <T extends { user: SessionUserData }>(data: T) => ({
  ...data,
  user: { ...data.user, attrs: data.user.attrs as UserAttrs },
});

export const login =
  (username: string, password: string, otp: string): AppThunk =>
  async (dispatch) => {
    const customerIdentifier = getCustomerIdentifier();

    const { data } = await client.mutate({
      mutation: LoginDocument,
      variables: { username, password, otp: otp || null },
    });

    if (data?.login) {
      setAuthorizationToken(data.login);

      const { data: sessionData } = await client.query({
        query: SessionDataDocument,
        variables: { customerIdentifier },
      });

      if (sessionData?.sessionData) {
        dispatch(loginSuccess(data.login, withTypedUserAttrs(sessionData.sessionData)));
      }
    }
  };

let beaconTimer: Nullable<number> = null;

export const loginSuccess =
  (token: string, { user, customer }: UserResponseType): AppThunk =>
  async (dispatch) => {
    setAuthorizationToken(token);
    setAuthorizationCookie(token);

    const sessionUser = { ...user, attrs: user.attrs as UserAttrs };

    dispatch(setCurrentUser(sessionUser));
    dispatch(getCustomerInfosSuccess(customer, sessionUser));

    if (userFeatureEnabled(user, "worktimes")) {
      dispatch(checkForUnconfirmedWorkTimes(user));
    }

    if (userFeatureEnabled(user, "roster") && "matchMedia" in window) {
      const mq = window.matchMedia("(max-width: 768px)");
      dispatch(setMobileView(mq.matches));
    }

    if (userFeatureEnabled(user, "roster_overview")) {
      dispatch(setAllCustomers(true));
    }

    try {
      if (user.customerId === "1") {
        await client.mutate({ mutation: RefreshUserProtocolBeaconDocument });

        if (beaconTimer) window.clearTimeout(beaconTimer);
        beaconTimer = window.setTimeout(
          async () => {
            await client.mutate({ mutation: RefreshUserProtocolBeaconDocument });
          },
          10 * 60 * 1000,
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

export const refreshLogin =
  (client: ApolloClient): AppThunk =>
  async (dispatch) => {
    const customerIdentifier = getCustomerIdentifier();

    try {
      const { data } = await client.mutate({
        mutation: RefreshDocument,
        variables: { customerIdentifier },
      });

      if (!data) {
        throw new Error();
      }

      dispatch(loginSuccess(data.refresh.token, withTypedUserAttrs(data.refresh)));
    } catch (error) {
      console.log(error);
      removeAuthorizationToken();
      return dispatch(setCurrentUser(null));
    }
  };

export const getCustomerInfosSuccess =
  (customer: SessionCustomer, user: Nullable<SessionUser>, path = document.location.pathname): AppThunk =>
  (dispatch) => {
    let project: SessionProject | null | undefined;

    if (!UNAUTHORIZED_PATHS.includes(path)) {
      const pieces = path.split("/").filter(notBlank);
      project = customer.projects.find((p) => p.identifier === pieces[1]);
    } else {
      const wantedProject =
        customer.projects.find((p) => p.id === user?.defaultProjectId) ||
        customer.projects.find((p) => p.id === customer.defaultProjectId) ||
        customer.projects[0];

      if (!user?.projects.find((p) => p.id === wantedProject.id) && !isCustomerAdmin(user, customer)) {
        project = user?.projects[0];
      } else {
        project = wantedProject;
      }
    }

    if (!project) {
      return dispatch(reducers.setNotFound(true));
    }

    dispatch(setCurrentCustomerAndProject(customer, project, false));
  };

export const setCurrentCustomerAndProjectByFinding =
  (customer: SessionCustomer, project: ProjectRef | MinimalProjectRef): AppThunk =>
  (dispatch) => {
    const foundProject = customer.projects.find((p) => p.id === project.id);

    if (!foundProject) {
      return dispatch(reducers.setNotFound(true));
    }

    dispatch(setCurrentCustomerAndProject(customer, foundProject));
  };

export const getNewUrl = (customer: CustomerRefWithIdentifier, project: SessionProject, session: SessionInterface) => {
  const uri = `/${customer.identifier}/${project.identifier}`;

  if (session.currentCustomer && session.currentProject) {
    return document.location.pathname.replace(
      new RegExp(`^/${session.currentCustomer.identifier}/${session.currentProject.identifier}(?=/|$)`),
      uri,
    );
  }

  if (UNAUTHORIZED_PATHS.includes(document.location.pathname)) {
    return null;
  }

  return document.location.pathname.replace(/^\/[\w-]+\/[\w-]+/, uri);
};

export const setCurrentCustomerAndProject =
  (customer: SessionCustomer, project: SessionProject, event = true): AppThunk =>
  (dispatch, getState) => {
    const state = getState();

    dispatch(reducers.setCurrentCustomerAndProject({ customer, project }));
    dispatch(setCustomer(customer));

    if (project.attrs.acd_user_id) {
      dispatch(getAcdToken(customer, project));
    } else {
      dispatch(resetAcdToken());
    }

    dispatch(
      resetCapacity({
        days: project?.attrs?.capacity_stats?.days || [],
        wanted: project?.attrs?.capacity_stats?.events_wanted || 0,
      }),
    );

    if (event) {
      const event = new CustomEvent("tt:customerOrProjectChanged", {
        detail: {
          newCustomer: customer,
          newProject: project,
          oldCustomer: state.session.currentCustomer,
          oldProject: state.session.currentProject,
        },
      });

      document.dispatchEvent(event);
    }
  };
